import { PropsWithChildren, useState } from "react";
import MenuTooltip, { MenuProps } from "./MenuTooltip";
import IconButton from "@/components/IconButton";
import Dialog from "@/components/Dialog";
import Button from "@/components/Button";

export type Actions = {
  primary: React.ReactNode;
  secondary?: React.ReactNode;
};

export type TooltipProps = Pick<MenuProps, "positioning" | "menuCorner" | "anchorCorner"> & {
  title?: string | React.ReactNode;
  supportingText: string | React.ReactNode;
  expandedSupportingText?: string | React.ReactNode;
  actions?: Actions;
  variant?: "rich" | "plain";
  color?: "light" | "dark" | "white" | "black";
  anchorClassName?: string;
  noVerticalFlip?: boolean;
  closeDelay?: number;
  compactHoverArea?: boolean;
  showExpand?: boolean;
  focusableAnchor?: boolean;
};

type ContentProps = Pick<TooltipProps, "title" | "supportingText" | "actions" | "variant"> & {
  showExpand?: boolean;
  onExpand?(): void;
};

const Content = ({ title, supportingText, actions, variant, showExpand, onExpand }: ContentProps) => (
  <div className="text-left">
    {!!title && (
      <div className={showExpand ? "flex items-center justify-between gap-2" : undefined}>
        <div className={`text-title-small whitespace-normal ${showExpand ? "min-w-0 flex-1" : ""}`}>{title}</div>
        {showExpand && (
          <IconButton
            name="open_in_full"
            ariaLabel="Expand"
            skipMargin
            className="-mr-1"
            containerClassName="shrink-0"
            data-testid="tooltip-expand"
            onClick={(e) => {
              e.stopPropagation();
              onExpand?.();
            }}
          />
        )}
      </div>
    )}
    <div className={`text-body-medium break-words ${variant === "rich" ? "mt-1" : "mt-0"}`}>{supportingText}</div>

    {!!actions && (
      <div className="mt-4">
        {actions.primary} {actions.secondary}
      </div>
    )}
  </div>
);

export default function Tooltip(props: PropsWithChildren<TooltipProps>) {
  const {
    title,
    supportingText,
    expandedSupportingText,
    actions,
    children,
    variant = "rich",
    color: colorProp,
    anchorClassName,
    noVerticalFlip,
    closeDelay,
    compactHoverArea,
    showExpand: showExpandProp,
    focusableAnchor,
    ...rest
  } = props;

  const [expandedOpen, setExpandedOpen] = useState(false);
  const [expandDialogMounted, setExpandDialogMounted] = useState(false);
  const showExpand = showExpandProp ?? (variant === "rich" && !!title);

  const color =
    colorProp === "white"
      ? "light"
      : colorProp === "black"
        ? "dark"
        : (colorProp ?? (variant === "plain" ? "dark" : "light"));

  return (
    <>
      <MenuTooltip
        AnchorComponent={children}
        content={
          <Content
            title={title}
            supportingText={supportingText}
            actions={actions}
            variant={variant}
            showExpand={showExpand}
            onExpand={() => {
              setExpandDialogMounted(true);
              setExpandedOpen(true);
            }}
          />
        }
        variant={variant}
        color={color}
        anchorClassName={anchorClassName}
        noVerticalFlip={noVerticalFlip}
        closeDelay={closeDelay}
        compactHoverArea={compactHoverArea}
        compactTopPadding={showExpand}
        focusableAnchor={focusableAnchor}
        {...rest}
      />
      {showExpand && expandDialogMounted && (
        <Dialog
          open={expandedOpen}
          onClosed={() => setExpandedOpen(false)}
          headline={title}
          actions={
            <Button variant="text" onClick={() => setExpandedOpen(false)}>
              Close
            </Button>
          }
        >
          <div className="max-w-[560px]">
            <div className="whitespace-pre-line text-body-medium">{expandedSupportingText ?? supportingText}</div>
            {!!actions && (
              <div className="mt-4">
                {actions.primary} {actions.secondary}
              </div>
            )}
          </div>
        </Dialog>
      )}
    </>
  );
}
